import { ParsedUrlQuery } from 'querystring'

import { useEffect } from 'react'

import { GetStaticPropsContext } from 'next'
import { useRouter } from 'next/router'

import { FlexContainer } from '@/components'
import { HeadTag } from '@/components/headTag'
import {
  CaseBannerContainer,
  CaseConclusionContainer,
  CasePageHeroContainer,
  ChallengeContainer,
  ObjectivesContainer,
  ResultsContainer,
} from '@/containers'
import {
  CaseBanner,
  Challenge,
  Color,
  Conclusion,
  Hero,
  Objectives,
  Results,
  getCasePage,
} from '@/service/api/casePage'
import { getPaths } from '@/service/api/paths'
import { Seo } from '@/service/types'
import { Constants } from '@/utils/constants'
import { withFooterHeader } from '@/utils/withFooterHeader'
import { withPageStaticProps } from '@/utils/withPageStaticProps'

export type CaseProps = {
  data: {
    hero: Hero
    challenge: Challenge
    caseBanner: CaseBanner
    objectives: Objectives
    results: Results
    conclusion: Conclusion
    color: Color
    seo: Seo
  }
}
interface Params extends ParsedUrlQuery {
  id: string
}

const Case: React.FC<CaseProps> = ({
  data: {
    hero,
    challenge,
    caseBanner,
    objectives,
    results,
    conclusion,
    color,
    seo,
  },
}) => {
  const { push, asPath } = useRouter()
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [asPath])

  const onPushNextCase = () => {
    if (!conclusion.slug) return
    push(`/case/${conclusion.slug}`)
  }
  return (
    <>
      <HeadTag
        languageOptions={[]}
        path={`/case/${conclusion.slug}`}
        title={seo?.metaTitle}
        description={seo?.metaDescription}
        image={seo?.metaImage}
      />
      <FlexContainer
        padding="112px 16px"
        direction="column"
        alignItems="center"
        style={{
          gap: '112px',
          backgroundColor: 'white',
          maxWidth: '100%',
        }}
      >
        {hero && <CasePageHeroContainer data={hero} color={color.primary} />}
        {challenge && (
          <ChallengeContainer data={challenge} color={color.primary} />
        )}
        {caseBanner && (
          <CaseBannerContainer data={caseBanner} color={color.caseBanner} />
        )}
        {objectives && (
          <ObjectivesContainer data={objectives} color={color.primary} />
        )}
        {results && <ResultsContainer data={results} color={color.primary} />}
        {conclusion && (
          <CaseConclusionContainer
            data={conclusion}
            color={color.primary}
            onPush={onPushNextCase}
            isNextCase={Boolean(conclusion?.slug)}
          />
        )}
      </FlexContainer>
    </>
  )
}

export async function getStaticPaths() {
  const { paths } = await getPaths('/cases')

  return {
    paths,
    fallback: 'blocking', // allow new pages to be added on runtime
  }
}

export const getStaticProps = withPageStaticProps(
  async (ctx: GetStaticPropsContext) => {
    const { params, locale } = ctx
    const { id } = params as Params

    const {
      hero,
      color,
      objectives,
      challenge,
      caseBanner,
      results,
      conclusion,
      seo,
    } = await getCasePage(id)

    return {
      props: {
        data: {
          color,
          hero,
          challenge,
          caseBanner,
          objectives,
          results,
          conclusion,
          seo,
        },
      },
      revalidate: Constants.ONE_DAY,
      notFound: locale === 'en',
    }
  },
)

export default withFooterHeader(Case)
